// ===========================================================================
// Type Definitions for Crowd/Mob Detection Whitebox Transparency Demo
// ===========================================================================

// ----------------------------- Agent Types ---------------------------------

/**
 * A person agent in the simulation world
 */
export interface AgentPerson {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  baseLabel: 'person';
  confBase: number;
  // Suspect tracking (hysteresis)
  suspectFrames: number;       // frames near weapon
  suspectPinFrames: number;    // frames to keep suspect label after leaving proximity
  isSuspect: boolean;
}

/**
 * A weapon marker in the simulation world
 */
export interface AgentWeapon {
  id: string;
  x: number;
  y: number;
  attachedTo?: string;  // person ID if attached
}

// ----------------------------- Detection Types -----------------------------

/**
 * A simulated detection (like YOLO output)
 */
export interface Detection {
  cls: 'person' | 'weapon';
  conf: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  cx: number;
  cy: number;
  agentId?: string;  // Reference to original agent
}

// ----------------------------- Tracking Types ------------------------------

/**
 * A tracked object with stable ID
 */
export interface Track {
  trackId: number;
  label: 'person' | 'suspect';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  cx: number;
  cy: number;
  age: number;          // frames since creation
  hits: number;         // consecutive hits
  lastSeen: number;     // frame index last seen
  confirmed: boolean;   // has enough hits
  history: Array<{ x: number; y: number; frame: number }>;  // position history
  agentId?: string;     // Reference to original agent
}

// ----------------------------- Crowd/Region Types --------------------------

/**
 * A detected crowd region (contour)
 */
export interface CrowdRegion {
  id: string;
  contour: Array<[number, number]>;  // points in world coordinates
  area: number;
  insideCount: number;               // gathering centroids inside
  mob: boolean;
  weaponInRegion: boolean;
  centroid: { x: number; y: number };
}

/**
 * A gathering group (connected component of people)
 */
export interface Gathering {
  id: string;
  memberTrackIds: number[];
  centroid: { x: number; y: number };
  depth: number;
  size: number;
}

// ----------------------------- Pipeline Thresholds -------------------------

/**
 * All configurable thresholds for the pipeline
 */
export interface PipelineThresholds {
  // Crowd detection
  crowdThreshold: number;           // min inside_count for crowd
  minGroupSize: number;             // min people for a gathering
  depthThreshold: number;           // max depth diff for same group
  spatialDistanceThreshold: number; // max pixel distance for grouping
  
  // Heatmap
  heatmapSigma: number;             // gaussian blur sigma
  heatmapThreshold: number;         // threshold for binary mask
  
  // Morphology
  morphKernelSize: number;          // dilation/erosion kernel
  
  // History smoothing
  historyLength: number;            // frames to average heatmap
  
  // Suspect detection
  suspectProximity: number;         // distance to weapon
  suspectConfirmFrames: number;     // frames near weapon to confirm
  suspectPinFrames: number;         // frames to keep suspect after leaving
}

/**
 * Simulation parameters
 */
export interface SimulationParams {
  detectionNoise: number;           // confidence noise stddev
  dropoutRate: number;              // probability of missing detection
  falsePositiveRate: number;        // probability of false positive
  agentSpeed: number;               // base movement speed
  depthMode: 'perspective-y' | 'radial';
  depthNoise: number;               // depth noise stddev
}

// ----------------------------- Frame Data ----------------------------------

/**
 * Complete data for a single frame (whitebox output)
 */
export interface FrameData {
  frameIndex: number;
  timestampMs: number;
  
  // Pipeline outputs
  detections: Detection[];
  tracks: Track[];
  weapons: Array<{ x: number; y: number; id: string }>;
  gatherings: Gathering[];
  
  // Grid data
  depthGrid: number[][];      // [rows][cols] e.g., 90x160
  heatmapGrid: number[][];    // same size
  binaryMask: boolean[][];    // thresholded heatmap
  
  // Crowd analysis
  crowdRegions: CrowdRegion[];
  
  // Alerts
  flags: {
    crowd: boolean;
    mob: boolean;
    weaponGlobal: boolean;
  };
  
  // Metrics
  metrics: {
    peopleCount: number;
    confirmedTracks: number;
    gatheringCount: number;
    contourCount: number;
    thresholds: PipelineThresholds;
  };
  
  // Whitebox reasoning
  reasoning: string[];
}

// ----------------------------- Simulation State ----------------------------

/**
 * Complete simulation world state
 */
export interface WorldState {
  people: AgentPerson[];
  weapons: AgentWeapon[];
  frameIndex: number;
  running: boolean;
  selectedAgentId: string | null;
  dragOffset: { x: number; y: number } | null;
}

/**
 * Tracker internal state
 */
export interface TrackerState {
  tracks: Map<number, Track>;
  nextTrackId: number;
  maxAge: number;
  minHits: number;
}

// ----------------------------- UI State ------------------------------------

/**
 * Inspector tab selection
 */
export type InspectorTab = 
  | 'detections'
  | 'tracks'
  | 'depth'
  | 'heatmap'
  | 'contours'
  | 'reasoning';

/**
 * Application UI state
 */
export interface UIState {
  inspectorTab: InspectorTab;
  selectedTrackId: number | null;
  showBoundingBoxes: boolean;
  showTrackIds: boolean;
  showTrackHistory: boolean;
  showContours: boolean;
  showDepthOverlay: boolean;
  showHeatmapOverlay: boolean;
}

// ----------------------------- Constants -----------------------------------

export const WORLD_WIDTH = 1280;
export const WORLD_HEIGHT = 720;
export const GRID_COLS = 160;
export const GRID_ROWS = 90;

export const DEFAULT_THRESHOLDS: PipelineThresholds = {
  crowdThreshold: 3,
  minGroupSize: 3,
  depthThreshold: 0.15,
  spatialDistanceThreshold: 100,
  heatmapSigma: 2.0,
  heatmapThreshold: 0.3,
  morphKernelSize: 3,
  historyLength: 5,
  suspectProximity: 80,
  suspectConfirmFrames: 15,
  suspectPinFrames: 30,
};

export const DEFAULT_SIM_PARAMS: SimulationParams = {
  detectionNoise: 0.05,
  dropoutRate: 0.02,
  falsePositiveRate: 0.01,
  agentSpeed: 1.0,
  depthMode: 'perspective-y',
  depthNoise: 0.02,
};

// Agent appearance
export const PERSON_RADIUS = 15;
export const WEAPON_SIZE = 12;
